import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ShowcaseSection = styled.section`
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionLabel = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.accentPurple};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: 0.02em;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.sm};
  }
`;

const MetricCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 16px;
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accentPurple};
    transform: translateY(-4px);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const MetricValue = styled.div`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  line-height: 1.2;
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.subtextGray};
  font-weight: ${props => props.theme.typography.weights.medium};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 0.8125rem;
  }
`;

const Showcase = () => {
  const metrics = [
    { value: '12+', label: 'Connected Tools' },
    { value: '8', label: 'White-Belt Modules' },
    { value: '2', label: 'Shareable Credentials' },
    { value: 'AI', label: 'Across the Stack' },
  ];

  return (
    <ShowcaseSection>
      <Container>
        <SectionHeader>
          <SectionLabel
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            The Platform
          </SectionLabel>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Built for Real Teams, End-to-End
          </SectionTitle>
        </SectionHeader>

        <MetricsRow>
          {metrics.map((metric, index) => (
            <MetricCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <MetricValue>{metric.value}</MetricValue>
              <MetricLabel>{metric.label}</MetricLabel>
            </MetricCard>
          ))}
        </MetricsRow>
      </Container>
    </ShowcaseSection>
  );
};

export default Showcase;
